import { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { Transition } from "@headlessui/react";
import { useClickAway } from "react-use";
import {
  Button,
  Input,
  Checkbox,
  Calendar,
  DateInput,
  useDateInput,
  NoMatchingResultIllustration,
  SearchIllustration,
  cn,
} from "ui";
import { format, parse } from "date-fns";
import { backendKeyMap } from "../utils/backendKeyMap";
import { snakeCaseToHumanReadable } from "../utils/snakeCaseToHumanReadable";
import { arrify } from "../utils/arrify";
import { axiosApiInstance } from "../pages/api/axios";
import {
  CalendarIcon,
  ChevronDownIcon,
  PlusCircleIcon,
  SearchIcon,
  XCircleIcon,
  XIcon,
} from "icons/outline";
import { useRouter } from "next/router";
import { useMoneyHashQuery } from "../hooks/useMoneyHashQuery";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const ChoicesList = ({
  choices,
  isMultiple,
  isSelected,
  selectedValue,
  handleSelect,
}) => {
  return (
    <ul className={cn("max-h-[180px] overflow-scroll px-2")}>
      {choices?.length ? (
        choices.map(([value, name]) => {
          return name ? (
            <li
              className={cn(
                "flex cursor-pointer items-center justify-between rounded-md px-2 py-2 text-sm",
                !isMultiple && value === selectedValue
                  ? "bg-info-bold text-foundations-inverse hover:bg-info-bolder"
                  : "text-foundations-subtle hover:bg-foundations-subtlest"
              )}
              key={value}
              onClick={() => handleSelect(value)}
            >
              <span>{snakeCaseToHumanReadable(name)}</span>
              {isMultiple ? (
                <Checkbox readOnly checked={isSelected(value)} />
              ) : null}
            </li>
          ) : null;
        })
      ) : (
        <div className="my-4 flex flex-col items-center space-y-4">
          <NoMatchingResultIllustration />
          <p className="text-sm text-foundations-subtle">
            No matching data available
          </p>
        </div>
      )}
    </ul>
  );
};

const SearchField = ({
  filter,
  selectedValue,
  isMultiple,
  isSelected,
  handleSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 450);

  const { data: options = [], isFetching } = useMoneyHashQuery({
    queryKey: [
      "filter",
      {
        path: filter.path,
        searchTerm: debouncedSearchTerm,
        param: filter.search_param,
      },
    ],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get(`${process.env.NEXT_PUBLIC_BACKEND_URL}${filter.path}`, {
          params: {
            [filter.search_param]: debouncedSearchTerm,
            offset: 0,
            limit: 10,
          },
          signal,
        })
        .then((res) => res.data),
    cacheTime: 0,
    enabled: !!searchTerm,
  });

  return (
    <div>
      <div className="px-4 pb-2">
        <Input
          autoFocus
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
          }}
          icon={<SearchIcon />}
          placeholder="Search"
        />
      </div>
      {!debouncedSearchTerm ? (
        <div className="my-4 flex flex-col items-center space-y-4">
          <SearchIllustration />
          <p className="text-center text-sm text-foundations-subtle">
            Please type a search keyword
          </p>
        </div>
      ) : isFetching ? (
        <div className="my-4 flex flex-col items-center">
          <SearchIllustration />
        </div>
      ) : (
        <ChoicesList
          choices={options}
          isMultiple={isMultiple}
          isSelected={isSelected}
          selectedValue={selectedValue}
          filter={filter}
          handleSelect={handleSelect}
        />
      )}
    </div>
  );
};

const multipleFieldsSet = new Set([
  "MultipleChoiceField",
  "ModelMultipleChoiceField",
  "MultiSelectSearchField",
]);

const fieldsWithoutFooterSet = new Set([
  "ChoiceField",
  "ModelChoiceField",
  "NullBooleanField",
  "DateField",
  "DateTimeField",
]);

const nullBooleanFieldOptions = [
  ["true", "Yes"],
  ["false", "No"],
];

const FilterInput = ({
  filter,
  value,
  onApplyFilter,
  shouldShowFooter,
  setOpen,
  isMultiple,
}) => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const searchInputRef = useRef(null);

  const updateFilter = (name, updatedValue) => {
    onApplyFilter(name, value === updatedValue ? "" : updatedValue);

    if (!shouldShowFooter) {
      setOpen(false);
    }
  };

  const isSelected = (optionValue) =>
    arrify(value)?.some?.((v) => v?.toString() === optionValue?.toString());

  const visibleChoices = filter.choices?.filter(([_value, name]) => {
    if (!searchKeyword) return true;

    // NOTE: convert to string as some values are non-string
    return name
      ?.toString()
      .toLocaleLowerCase()
      .includes(searchKeyword.toLocaleLowerCase());
  });

  const handleSelect = (selectedValue) => {
    if (!isMultiple) {
      updateFilter(filter.field_name, selectedValue);
      return;
    }

    if (isSelected(selectedValue)) {
      const selectedOptionsUpdated = arrify(value).filter(
        (v) => v !== selectedValue
      );

      updateFilter(filter.field_name, selectedOptionsUpdated);
      return;
    }

    const selectedOptionsUpdated = [...arrify(value), selectedValue];
    updateFilter(filter.field_name, selectedOptionsUpdated);
  };

  useEffect(() => {
    if (!searchInputRef.current) return;
    searchInputRef.current.focus();
  }, [searchInputRef]);

  if (filter.field_type === "CharField") {
    return (
      <div className="mb-2 px-4">
        <Input
          autoFocus
          type="text"
          placeholder="Add text..."
          value={value || ""}
          onChange={(value) => {
            updateFilter(filter.field_name, value);
          }}
        />
      </div>
    );
  } else if (filter.field_type === "DecimalField") {
    return (
      <div className="mb-2 px-4">
        <Input
          autoFocus
          type="number"
          value={value || ""}
          placeholder="Enter number..."
          onChange={(value) => {
            updateFilter(filter.field_name, value);
          }}
        />
      </div>
    );
  } else if (
    filter.field_type === "DateTimeField" ||
    filter.field_type === "DateField"
  ) {
    return (
      <DateField
        value={value}
        updateFilter={updateFilter}
        filter={filter}
        close={() => setOpen(false)}
      />
    );
  } else if (
    filter.field_type === "ChoiceField" ||
    filter.field_type === "MultipleChoiceField" ||
    filter.field_type === "ModelChoiceField" ||
    filter.field_type === "ModelMultipleChoiceField"
  ) {
    return (
      <div>
        {filter.choices?.length > 2 ? (
          <div className="px-4 pb-2">
            <Input
              autoFocus
              ref={searchInputRef}
              value={searchKeyword}
              placeholder="Search..."
              onChange={(value) => {
                setSearchKeyword(value);
              }}
              icon={<SearchIcon />}
            />
          </div>
        ) : null}
        <ChoicesList
          choices={visibleChoices}
          isMultiple={isMultiple}
          isSelected={isSelected}
          filter={filter}
          selectedValue={value}
          handleSelect={handleSelect}
        />
      </div>
    );
  } else if (filter.field_type === "NullBooleanField") {
    return (
      <div>
        <ChoicesList
          choices={nullBooleanFieldOptions}
          isMultiple={isMultiple}
          isSelected={isSelected}
          selectedValue={value}
          filter={filter}
          handleSelect={handleSelect}
        />
      </div>
    );
  } else if (
    ["SearchField", "MultiSelectSearchField"].includes(filter.field_type)
  ) {
    return (
      <SearchField
        filter={filter}
        selectedValue={value}
        isSelected={isSelected}
        handleSelect={handleSelect}
        isMultiple={isMultiple}
      />
    );
  }
};

function FilterField({ filter, value, onRemoveFilter, onApplyFilter }) {
  const [tempValue, setTempValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const closeFilterWithInitialState = () => {
    // reset the temp value again in case reset was clicked
    setTempValue(value);
    setIsOpen(false);
  };
  useClickAway(containerRef, () => {
    closeFilterWithInitialState();
  });

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const isMultiple = multipleFieldsSet.has(filter.field_type);
  const shouldShowFooter = !fieldsWithoutFooterSet.has(filter.field_type);

  const label =
    backendKeyMap[filter.field_name] ??
    snakeCaseToHumanReadable(filter.field_name);

  const displayedValue = useMemo(() => {
    if (!value) return null;

    if (filter.field_type === "SearchField") {
      return filter.value_lookup;
    }

    if (filter.field_type === "MultiSelectSearchField") {
      return filter.multiple_selected_values.map((v) => Object.values(v)[0]);
    }

    if (filter.field_type === "NullBooleanField")
      return nullBooleanFieldOptions.find((option) => option[0] === value)?.[1];

    if (!filter.choices) return value;

    // Get option label corresponding to it's value
    if (!isMultiple)
      return filter.choices.find(
        // NOTE: convert to string as some values are non-string
        (option) => option[0]?.toString() === value?.toString()
      )?.[1];

    return arrify(value).map(
      (v) =>
        filter.choices.find(
          // NOTE: convert to string as some values are non-string
          (option) => option[0]?.toString() === v?.toString()
        )?.[1]
    );
  }, [filter, value, isMultiple]);

  return (
    <div ref={containerRef} className="relative mb-1 inline-flex">
      <button
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        className={cn(
          "group mr-2	inline-flex shrink-0 cursor-pointer items-center  justify-center rounded-full border  px-2 py-1 hover:shadow focus:outline-none focus:ring-info-bold focus-visible:ring-2",
          displayedValue
            ? "border-foundations-subtle bg-foundations-subtlest hover:border-foundations-subtle"
            : isOpen
              ? "border-info-bold bg-foundations-subtlest ring-1 ring-info-bold hover:border-info-bold"
              : "border-transparent bg-foundations-default hover:border-foundations-subtler"
        )}
      >
        {displayedValue ? (
          <span
            aria-label={`Clear filter ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFilter(filter.field_name);
              setIsOpen(false);
            }}
          >
            <XCircleIcon
              className={cn(
                isOpen
                  ? "text-info"
                  : "text-foundations-subtler group-hover:text-foundations-subtle"
              )}
            />
          </span>
        ) : (
          <PlusCircleIcon
            className={cn(
              isOpen
                ? "text-info"
                : "text-foundations-subtler group-hover:text-foundations-subtle"
            )}
          />
        )}
        <label
          htmlFor={filter.field_name}
          className={cn(
            "block cursor-pointer pl-1 text-sm font-medium capitalize text-foundations-subtle group-hover:text-foundations-bold group-focus:text-foundations-bold",
            (displayedValue || isOpen) && "text-foundations-bold"
          )}
        >
          <span>{label}</span>
          {displayedValue && (
            <>
              <span className="px-1">|</span>
              <span className="text-info">
                {isMultiple ? (
                  <MultiplePreview value={arrify(displayedValue)} />
                ) : (
                  snakeCaseToHumanReadable(displayedValue)
                )}
              </span>
            </>
          )}
        </label>
        {displayedValue && (
          <ChevronDownIcon
            className={cn(
              "text-sm",
              isOpen
                ? "text-info"
                : "text-foundations-subtler group-hover:text-foundations-subtle"
            )}
            height={16}
          />
        )}
      </button>
      <Transition
        as={Fragment}
        show={isOpen}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div className="absolute left-0 z-20 mt-10 origin-top-left rounded-md bg-foundations-default shadow-lg focus:outline-none">
          <div className="w-[320px]">
            <div className="flex flex-col">
              <div className="flex justify-between p-4 pb-1">
                <div className="font-medium text-foundations-bolder">
                  Filter by {label}
                </div>
                <div className="text-foundations-subtler hover:text-foundations-subtle">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="focus:text-info focus:outline-none"
                  >
                    <XCircleIcon />
                  </button>
                </div>
              </div>
              <div className={cn(shouldShowFooter ? "" : "pb-2")}>
                <FilterInput
                  filter={filter}
                  value={tempValue}
                  onApplyFilter={(filter, value) => {
                    setTempValue(value);
                    if (!shouldShowFooter) {
                      onApplyFilter(filter, value);
                    }
                  }}
                  shouldShowFooter={shouldShowFooter}
                  setOpen={setIsOpen}
                  isMultiple={isMultiple}
                />
              </div>
              {shouldShowFooter && (
                <div className="mt-1 flex justify-end border-t border-foundations-subtle p-4">
                  <div className="mr-3">
                    {isMultiple && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setTempValue("");
                        }}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      onApplyFilter(filter.field_name, tempValue);
                      setIsOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}

const MultiplePreview = ({ value }) => {
  if (!value?.length) return "";
  if (value.length === 1) {
    return value[0];
  }

  return (
    <>
      {value[0]} <span className="lowercase">and {value.length - 1} more</span>
    </>
  );
};

export const Filters = ({ filters }) => {
  const router = useRouter();
  const visibleFilters = useMemo(
    () =>
      filters.filter(
        (f) => !["created", "date_created", "is_live"].includes(f.field_name)
      ),
    [filters]
  );

  const isAnyFilterApplied = useMemo(
    () => filters.some(({ field_name }) => !!router.query[field_name]),
    [filters, router.query]
  );

  const applyFilter = (filter, value) => {
    if (!value) return removeFilter(filter);
    router.replace({ query: { ...router.query, page: 1, [filter]: value } });
  };

  const removeFilter = (filter) => {
    const { [filter]: _queryFiltered, ...restQuery } = router.query;
    router.replace({ query: { ...restQuery, page: 1 } });
  };

  const clearFilters = () => {
    const { search } = router.query;
    router.replace({
      query: {
        page: 1,
        ...(search ? { search } : {}), //persist search if exists
      },
    });
  };

  if (!visibleFilters.length) return null;

  return (
    <div className="flex items-start pb-4 text-left">
      <div className=" mr-2 mt-1 text-foundations-subtler">Filter:</div>
      <div className="basis-10/12">
        {visibleFilters.map((filter, index) => (
          <FilterField
            key={filter.field_type + index}
            filter={filter}
            value={router.query[filter.field_name]}
            onRemoveFilter={removeFilter}
            onApplyFilter={applyFilter}
          />
        ))}
      </div>
      <div className="flex basis-2/12 items-center justify-end text-foundations-subtle hover:text-foundations-bold">
        {isAnyFilterApplied && (
          <button className="flex items-center" onClick={clearFilters}>
            <XIcon className="h-5 text-xs" />{" "}
            <span className="pl-1">Clear filters</span>
          </button>
        )}
      </div>
    </div>
  );
};

export const InPlaceFilters = ({
  filters,
  showLabel = true,
  showClearFilters = true,
  appliedFilters,
  customApplyFilter,
  customRemoveFilter,
  customClearFilters,
}) => {
  const visibleFilters = useMemo(
    () =>
      filters.filter(
        (f) => !["created", "date_created", "is_live"].includes(f.field_name)
      ),
    [filters]
  );

  const applyFilter = (filter, value) => {
    if (!value) return removeFilter(filter);
    customApplyFilter(filter, value);
  };

  const removeFilter = (filter) => {
    customRemoveFilter(filter);
  };

  const clearFilters = () => {
    customClearFilters();
  };

  if (!visibleFilters.length) return null;

  return (
    <div className="flex items-start pb-4 text-left">
      {showLabel && (
        <div className="mr-2 mt-1 text-foundations-subtler">Filter:</div>
      )}
      <div className="basis-10/12">
        {visibleFilters.map((filter, index) => (
          <FilterField
            key={filter.field_type + index}
            filter={filter}
            value={appliedFilters[filter.field_name]}
            onRemoveFilter={removeFilter}
            onApplyFilter={applyFilter}
          />
        ))}
      </div>
      <div className="flex basis-2/12 items-center justify-end text-foundations-subtle hover:text-foundations-bold">
        {showClearFilters && (
          <button className="flex items-center" onClick={clearFilters}>
            <XIcon className="h-5 text-xs" />{" "}
            <span className="pl-1">Clear filters</span>
          </button>
        )}
      </div>
    </div>
  );
};

const DateField = ({ value, updateFilter, filter }) => {
  const { inputRef, inputProps, setInputValue, month, setMonth } = useDateInput(
    {
      onSelect: (day) => updateFilter(filter.field_name, day),
      selected: value,
    }
  );

  const handleDaySelect = (date) => {
    if (date) {
      updateFilter(
        filter.field_name,
        format(date, useDateInput.valueFormatString)
      );
    } else {
      updateFilter(filter.field_name, "");
      setInputValue("");
    }
  };

  return (
    <div className="flex flex-col">
      <DateInput
        ref={inputRef}
        className="px-2"
        icon={<CalendarIcon className="h-5 w-5" />}
        {...inputProps}
      />
      <Calendar
        className="self-center"
        mode="single"
        selected={
          value
            ? parse(value, useDateInput.valueFormatString, new Date())
            : undefined
        }
        onSelect={handleDaySelect}
        disablePortal
        month={month}
        onMonthChange={setMonth}
      />
    </div>
  );
};
